import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import InboxesAPI from '../../api/inboxes';
import WebChannel from '../../api/channel/webChannel';
import FBChannel from '../../api/channel/fbChannel';
import TwilioChannel from '../../api/channel/twilioChannel';
import InstagramChannel from '../../api/channel/instagramChannel';
import { throwErrorMessage } from '../utils/api';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';

// Local cap on how long an optimistic template lingers in the getter if Meta
// never returns it (rejected silently, network gap, etc). State dies on reload,
// so this is only a guard for long-running sessions.
export const OPTIMISTIC_TEMPLATE_RETENTION_MS = 30 * 60 * 1000;

// Silent auto-refresh schedule (3 cycles × 60s after a create) lives at module
// scope because timeout handles are not serializable, not reactive, and don't
// belong in Vuex state. We pair each schedule with a generation counter so a
// tick that's mid-`await` can detect it's been cancelled or superseded — without
// the counter, clearTimeout can't reach the next iteration scheduled after the
// await resolves, and the cancel silently leaks a fresh tick chain.
const optimisticRefreshTimeouts = new Map();
const optimisticRefreshGenerations = new Map();

const classifyWhatsAppError = errorData => {
  if (!errorData.code || !errorData.subcode) return null;

  const code = errorData.code;
  const subcode = errorData.subcode;

  if (code === 100) {
    switch (subcode) {
      case 2388124:
        return 'TEMPLATE_EDIT_RATE_LIMIT';
      case 2388105:
        return 'TEMPLATE_NAME_EXISTS';
      case 2388001:
        return 'TEMPLATE_FORMAT_INVALID';
      case 2388103:
        return 'TEMPLATE_CONTENT_INVALID';
      default:
        return 'TEMPLATE_VALIDATION_ERROR';
    }
  }

  return 'UNKNOWN_ERROR';
};

const extractErrorMessage = error => {
  if (error.response?.data?.error) {
    const errorData = error.response.data.error;

    if (typeof errorData === 'object' && errorData.message) {
      const classification = classifyWhatsAppError(errorData);
      return {
        message: errorData.message,
        classification: classification,
        code: errorData.code,
        subcode: errorData.subcode,
        details: errorData.details,
        user_action: errorData.user_action,
      };
    }

    if (typeof errorData === 'string') {
      return { message: errorData };
    }
  }

  return { message: error.message || 'An unexpected error occurred' };
};

const extractTemplatesFromRecord = record => {
  if (!record) {
    return [];
  }

  const topLevelTemplates = Array.isArray(record.message_templates)
    ? record.message_templates
    : [];
  const additionalAttributeTemplates = Array.isArray(
    record.additional_attributes?.message_templates
  )
    ? record.additional_attributes.message_templates
    : [];

  if (topLevelTemplates.length) {
    return topLevelTemplates;
  }

  if (additionalAttributeTemplates.length) {
    return additionalAttributeTemplates;
  }

  return [];
};

const applyTemplatesToRecord = (record, sourceRecord) => {
  if (sourceRecord?.message_templates?.length) {
    return {
      ...record,
      message_templates: sourceRecord.message_templates,
    };
  }

  if (sourceRecord?.additional_attributes?.message_templates?.length) {
    return {
      ...record,
      additional_attributes: {
        ...record.additional_attributes,
        message_templates: sourceRecord.additional_attributes.message_templates,
      },
    };
  }

  return record;
};

const mergeTemplatesFromExistingRecords = (existingRecords, newRecords) => {
  if (
    !Array.isArray(existingRecords) ||
    !existingRecords.length ||
    !Array.isArray(newRecords)
  ) {
    return newRecords;
  }

  const existingRecordsMap = existingRecords.reduce((acc, record) => {
    acc[record.id] = record;
    return acc;
  }, {});

  return newRecords.map(record => {
    const existingRecord = existingRecordsMap[record.id];
    if (!existingRecord) {
      return record;
    }

    const existingTemplates = extractTemplatesFromRecord(existingRecord);
    const incomingTemplates = extractTemplatesFromRecord(record);

    if (!existingTemplates.length || incomingTemplates.length) {
      return record;
    }

    return applyTemplatesToRecord(record, existingRecord);
  });
};

const buildInboxData = inboxParams => {
  const formData = new FormData();
  const { channel = {}, ...inboxProperties } = inboxParams;
  Object.keys(inboxProperties).forEach(key => {
    formData.append(key, inboxProperties[key]);
  });
  const { selectedFeatureFlags, ...channelParams } = channel;
  if (selectedFeatureFlags) {
    if (selectedFeatureFlags.length) {
      selectedFeatureFlags.forEach(featureFlag => {
        formData.append(`channel[selected_feature_flags][]`, featureFlag);
      });
    } else {
      formData.append('channel[selected_feature_flags][]', '');
    }
  }
  Object.keys(channelParams).forEach(key => {
    formData.append(`channel[${key}]`, channel[key]);
  });
  return formData;
};

export const state = {
  records: [],
  // Optimistic templates created in this session, keyed by inboxId. Merged into
  // getWhatsAppTemplates so a newly-created template shows up immediately while
  // Meta's eventual-consistency window catches up. Cleared on reload.
  recentlyCreatedTemplates: {},
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
  },
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getWhatsAppTemplates: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );

    const {
      message_templates: whatsAppMessageTemplates,
      additional_attributes: additionalAttributes,
    } = inbox || {};

    const { message_templates: apiInboxMessageTemplates } =
      additionalAttributes || {};
    const messagesTemplates =
      whatsAppMessageTemplates || apiInboxMessageTemplates;

    const baseTemplates =
      messagesTemplates instanceof Array ? messagesTemplates : [];

    if (inbox?.channel_type === 'Channel::TwilioSms') {
      return baseTemplates.map(template => ({
        ...template,
        isTwilioTemplate: true,
      }));
    }

    // Merge optimistic templates created in this session. Drop ones that Meta
    // has already returned (match by id) or that exceeded the retention window.
    const optimistic =
      $state.recentlyCreatedTemplates?.[String(inboxId)] || [];
    if (!optimistic.length) return baseTemplates;

    const baseIds = new Set(baseTemplates.map(template => template.id));
    const now = Date.now();
    const liveOptimistic = optimistic.filter(template => {
      if (baseIds.has(template.id)) return false;
      const age = now - (template._optimisticInsertedAt || 0);
      return age <= OPTIMISTIC_TEMPLATE_RETENTION_MS;
    });

    return [...liveOptimistic, ...baseTemplates];
  },
  getNewConversationInboxes($state) {
    return $state.records.filter(inbox => {
      const { channel_type: channelType, phone_number: phoneNumber = '' } =
        inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      const isSmsChannel =
        channelType === INBOX_TYPES.TWILIO &&
        phoneNumber.startsWith('whatsapp');
      return isEmailChannel || isSmsChannel;
    });
  },
  getInbox: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    return inbox || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getEmailInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.EMAIL
    );
  },
  getWebsiteInboxes($state) {
    return $state.records.filter(item => item.channel_type === INBOX_TYPES.WEB);
  },
  getTwilioInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  getSMSInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.SMS ||
        (item.channel_type === INBOX_TYPES.TWILIO && item.medium === 'sms')
    );
  },
  getWhatsappInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.WHATSAPP ||
        item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  dialogFlowEnabledInboxes($state) {
    return $state.records.filter(
      item => item.channel_type !== INBOX_TYPES.EMAIL
    );
  },
};

const sendAnalyticsEvent = channelType => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

export const actions = {
  revalidate: async ({ commit }, { newKey }) => {
    try {
      const isExistingKeyValid = await InboxesAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await InboxesAPI.refetchAndCommit(newKey);
        commit(types.default.SET_INBOXES, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },
  get: async ({ commit }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get(true);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      commit(types.default.SET_INBOXES, response.data.payload);
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  getWithTemplates: async ({ commit }, inboxId) => {
    const response = await InboxesAPI.getWithTemplates(inboxId);
    commit(types.default.SET_INBOXES_ITEM, response.data);
    return response;
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(errorMessage);
    }
  },
  createWebsiteChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(buildInboxData(params));
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('website');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return throwErrorMessage(error);
    }
  },
  createTwilioChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await TwilioChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('twilio');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  createFBChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await FBChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('facebook');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  createInstagramChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await InstagramChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('instagram');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      await InboxesAPI.delete(inboxId);
      commit(types.default.DELETE_INBOXES, inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });

      if (error?.response?.data?.code === 'INBOX_CONTINUITY_CONNECTED')
        throw error?.response?.data?.inboxes_names?.toString();
      throw new Error(error);
    }
  },
  reauthorizeFacebookPage: async ({ commit }, params) => {
    try {
      const response = await FBChannel.reauthorizeFacebookPage(params);
      commit(types.default.EDIT_INBOXES, response.data);
    } catch (error) {
      throw new Error(error.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  syncWhatsAppTemplates: async ({ commit }, payload) => {
    // Accept either a bare inboxId (legacy callers) or { inboxId, silent }.
    // `silent` is a contract with the caller — they handle alerts, we never do.
    const inboxId =
      payload && typeof payload === 'object' ? payload.inboxId : payload;

    let response;
    let syncError;
    try {
      response = await InboxesAPI.syncWhatsAppTemplates(inboxId);
    } catch (error) {
      syncError = error;
    }

    try {
      const inboxResponse = await InboxesAPI.getWithTemplates(inboxId);
      commit(types.default.SET_INBOXES_ITEM, inboxResponse.data);
    } catch (error) {
      // Silently fail template fetch - don't block the response
      // Store update is best-effort; sync error takes precedence
    }

    if (syncError) {
      throw syncError;
    }
    return response.data;
  },
  scheduleOptimisticTemplateRefresh: (
    { dispatch },
    { inboxId, cycles = 3, intervalMs = 60000 } = {}
  ) => {
    if (!inboxId) return;
    const key = String(inboxId);
    // Cancel any pending schedule for this inbox so we don't stack timers.
    const existing = optimisticRefreshTimeouts.get(key);
    if (existing) clearTimeout(existing);
    // Bump generation: any prior tick that's mid-`await dispatch` will bail
    // out when it reads this value and finds it changed.
    const myGeneration =
      (optimisticRefreshGenerations.get(key) || 0) + 1;
    optimisticRefreshGenerations.set(key, myGeneration);

    const tick = remaining => {
      // Stop if this chain has been superseded or cancelled.
      if (optimisticRefreshGenerations.get(key) !== myGeneration) return;
      if (remaining <= 0) {
        optimisticRefreshTimeouts.delete(key);
        return;
      }
      const handle = setTimeout(async () => {
        try {
          await dispatch('syncWhatsAppTemplates', { inboxId, silent: true });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(
            '[whatsapp-templates] silent refresh failed',
            error?.message || error
          );
        }
        tick(remaining - 1);
      }, intervalMs);
      optimisticRefreshTimeouts.set(key, handle);
    };

    tick(cycles);
  },
  cancelOptimisticTemplateRefresh: (_ctx, inboxId) => {
    if (!inboxId) return;
    const key = String(inboxId);
    const handle = optimisticRefreshTimeouts.get(key);
    if (handle) {
      clearTimeout(handle);
      optimisticRefreshTimeouts.delete(key);
    }
    // Invalidate any in-flight tick that may be inside an `await dispatch`.
    optimisticRefreshGenerations.set(
      key,
      (optimisticRefreshGenerations.get(key) || 0) + 1
    );
  },
  createWhatsAppTemplate: async (
    { commit },
    { inboxId, templateData, isFormData = false, rawTemplate = null }
  ) => {
    try {
      const response = await InboxesAPI.createWhatsAppTemplate(
        inboxId,
        templateData,
        isFormData
      );

      // Optimistic insert: stamp the template locally so it shows up in the
      // list immediately, before Meta's listing endpoint catches up (which
      // can take 30s-15min). The getter filters this out once Meta returns
      // the same id, or after OPTIMISTIC_TEMPLATE_RETENTION_MS.
      const metaData = response?.data?.data || {};
      if (rawTemplate?.name && metaData.id) {
        commit(types.default.ADD_OPTIMISTIC_TEMPLATE, {
          inboxId,
          template: {
            id: metaData.id,
            name: rawTemplate.name,
            language: rawTemplate.language,
            category: metaData.category || rawTemplate.category,
            status: metaData.status || 'PENDING',
            components: rawTemplate.components || [],
            _optimisticInsertedAt: Date.now(),
          },
        });
      }

      const inboxResponse = await InboxesAPI.show(inboxId);
      commit(types.default.EDIT_INBOXES, inboxResponse.data);
      return response.data;
    } catch (error) {
      const errorInfo = extractErrorMessage(error);
      const classification = classifyWhatsAppError(errorInfo);

      // Create a structured error object with classification for proper handling
      const structuredError = {
        ...errorInfo,
        classification: classification,
      };

      throw structuredError;
    }
  },
  updateWhatsAppTemplate: async (
    { commit },
    { inboxId, templateId, templateData, isFormData = false }
  ) => {
    try {
      const response = await InboxesAPI.updateWhatsAppTemplate(
        inboxId,
        templateId,
        templateData,
        isFormData
      );
      const inboxResponse = await InboxesAPI.show(inboxId);
      commit(types.default.EDIT_INBOXES, inboxResponse.data);
      return response.data;
    } catch (error) {
      const errorInfo = extractErrorMessage(error);
      const classification = classifyWhatsAppError(errorInfo);

      const structuredError = {
        ...errorInfo,
        classification: classification,
      };

      throw structuredError;
    }
  },
  deleteWhatsAppTemplate: async ({ commit }, { inboxId, template }) => {
    // Drop the optimistic entry first so deleting a still-pending template
    // removes it from the UI even if the backend delete hasn't propagated yet.
    if (template?.id) {
      commit(types.default.REMOVE_OPTIMISTIC_TEMPLATE, {
        inboxId,
        templateId: template.id,
      });
    }
    try {
      const response = await InboxesAPI.deleteWhatsAppTemplate(
        inboxId,
        template
      );
      const inboxResponse = await InboxesAPI.show(inboxId);
      commit(types.default.EDIT_INBOXES, inboxResponse.data);
      return response.data;
    } catch (error) {
      const errorInfo = extractErrorMessage(error);
      const classification = classifyWhatsAppError(errorInfo);

      const structuredError = {
        ...errorInfo,
        classification: classification,
      };

      throw structuredError;
    }
  },
  retryWhatsappConnection: async (_, { inboxId }) => {
    try {
      const response = await InboxesAPI.retryWhatsappConnection(inboxId);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
  reconnectWhatsapp: async ({ commit }, { inboxId, ...params }) => {
    try {
      const response = await InboxesAPI.reconnectWhatsapp(inboxId, params);
      const inboxResponse = await InboxesAPI.show(inboxId);
      commit(types.default.EDIT_INBOXES, inboxResponse.data);
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.error || error.message);
    }
  },
  resubscribeInstagram: async (_, { inboxId }) => {
    try {
      const response = await InboxesAPI.resubscribeInstagram(inboxId);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
};

const setInboxesRecords = ($state, data) => {
  $state.records = mergeTemplatesFromExistingRecords($state.records, data);
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: setInboxesRecords,
  [types.default.SET_INBOXES_ITEM]: MutationHelpers.setSingleRecord,
  [types.default.ADD_INBOXES]($state, data) {
    const exists = $state.records.some(record => record.id === data.id);
    if (!exists) {
      $state.records.push(data);
    }
  },
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
  [types.default.ADD_OPTIMISTIC_TEMPLATE]($state, { inboxId, template }) {
    if (!template?.id) return;
    const key = String(inboxId);
    const existing = $state.recentlyCreatedTemplates[key] || [];
    const deduped = existing.filter(item => item.id !== template.id);
    // Stamp defensively — without a timestamp, the getter treats the entry as
    // expired (age = now - 0) and filters it out silently. Caller is expected
    // to set this, but we guarantee it here so a missing stamp never hides
    // the optimistic row.
    const stamped = template._optimisticInsertedAt
      ? template
      : { ...template, _optimisticInsertedAt: Date.now() };
    $state.recentlyCreatedTemplates = {
      ...$state.recentlyCreatedTemplates,
      [key]: [stamped, ...deduped],
    };
  },
  [types.default.REMOVE_OPTIMISTIC_TEMPLATE]($state, { inboxId, templateId }) {
    const key = String(inboxId);
    const existing = $state.recentlyCreatedTemplates[key];
    if (!existing?.length) return;
    $state.recentlyCreatedTemplates = {
      ...$state.recentlyCreatedTemplates,
      [key]: existing.filter(item => item.id !== templateId),
    };
  },
  [types.default.CLEAR_OPTIMISTIC_TEMPLATES]($state, inboxId) {
    const key = String(inboxId);
    if (!$state.recentlyCreatedTemplates[key]) return;
    const { [key]: _removed, ...rest } = $state.recentlyCreatedTemplates;
    $state.recentlyCreatedTemplates = rest;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
